'use client';

import { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { createPortal } from 'react-dom';
import { Modal } from '@/components/atoms/Modal/Modal';
import { useTranslations } from 'next-intl';
import { Typography } from '@/components/atoms/Typography/Typography';
import { usePageStore } from '@/store/usePageStore';
import { localizedSlugs } from '@/i18n/routing';
import * as Styled from './ModalNavigationAlert.styled';


export function ModalNavigationAlert() {
  const router = useRouter();
  const [modalRoot, setModalRoot] = useState<HTMLElement | null>(null);
  const t = useTranslations('privateList');
  const stateSelector = usePageStore;
  const showNavigationModalAlert = stateSelector((s) => s.showNavigationModalAlert);
  const [displayNavigationModalAlert, setDisplayNavigationModalAlert] = useState<boolean>(false);
  const setShowNavigationModalAlert = stateSelector((s) => s.setShowNavigationModalAlert);
  const urlOnHoldFromPresentation = stateSelector((s) => s.urlOnHoldFromPresentation);
  const setUrlOnHoldFromPresentation = stateSelector((s) => s.setUrlOnHoldFromPresentation);
  const pathName = usePathname();

  // On mount, store reference to portal target
  useEffect(() => {
    setModalRoot(document.getElementById('modal-navigation-alert-root'));
  }, []);

  // ensure that it triggers only in a presentation or project page
  useEffect(() => {
    if (!showNavigationModalAlert) {
      setDisplayNavigationModalAlert(false);
      return;
    }
    if (showNavigationModalAlert && (pathName.includes(`${localizedSlugs.presentation.en}/`) || pathName.includes(`${localizedSlugs.presentation.es}/`))) {
      setDisplayNavigationModalAlert(true);
      return;
    }
    if (showNavigationModalAlert && pathName.includes(`${localizedSlugs.projects.en}/`) || pathName.includes(`${localizedSlugs.projects.es}/`)) {
      setDisplayNavigationModalAlert(true);
      return;
    }
    setDisplayNavigationModalAlert(showNavigationModalAlert)
  }, [showNavigationModalAlert, pathName]);

  const handleConfirm = () => {
    setShowNavigationModalAlert(false);
    if (urlOnHoldFromPresentation) {
      usePageStore.getState().setIsPresentationMode(false);
      router.push(urlOnHoldFromPresentation);
      setUrlOnHoldFromPresentation(undefined);
    }
  };

  const handleCancel = () => {
    setShowNavigationModalAlert(false);
    if (urlOnHoldFromPresentation) {
      setUrlOnHoldFromPresentation(undefined);
    }  
  };

  const modalContent = (
    <Modal displayModal={displayNavigationModalAlert} closeOnOutsideClick={false}>
      <Styled.ModalContent>
        <Typography text={t('alertMessage')} size="lg" />
        <Typography text={t('alertQuestion')} size="lg" />
        <Styled.ButtonsContainer>
          <Styled.Button onClick={handleCancel}>
            <Typography text={t('buttonNo')} size="lg" color="primary" />
          </Styled.Button>
          <Styled.Button onClick={handleConfirm}>
            <Typography text={t('buttonYes')} size="lg" color="white" />
          </Styled.Button>
        </Styled.ButtonsContainer>
      </Styled.ModalContent>
    </Modal>
  );

  return modalRoot ? createPortal(modalContent, modalRoot) : null;
}
